import React, { useEffect, useState } from "react";

import HeadquartersPage from "../components/headquarters/HeadquartersPage";
import HeadquartersSection from "../components/headquarters/HeadquartersSection";
import HeadquartersGrid from "../components/headquarters/HeadquartersGrid";
import HeadquartersMetricCard from "../components/headquarters/HeadquartersMetricCard";
import StatusPill from "../components/headquarters/StatusPill";

export default function ProductsServices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);

  const [form, setForm] = useState({
    item_type: "service",
    name: "",
    description: "",
    unit: "",
    default_quantity: 1,
    default_rate: 0,
    cost: 0,
    taxable: false,
    sku: "",
    active: true,
  });

  async function loadItems() {
    try {
      const res = await fetch("/api/products-services/", {
        credentials: "include",
        headers: {
          "x-tenant":
            window.location.hostname === "localhost"
              ? "dfasio"
              : window.location.hostname.split(".")[0] || "root",
        },
      });

      const data = await res.json();

      if (!Array.isArray(data)) {
        console.error("Products & Services API returned:", data);
        setItems([]);
        return;
      }

      setItems(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadItems();
  }, []);

  function startEdit(item) {
    setEditingId(item.id);
    setEditForm({
      item_type: item.item_type || "service",
      name: item.name || "",
      description: item.description || "",
      unit: item.unit || "",
      default_quantity: item.default_quantity || 1,
      default_rate: item.default_rate || 0,
      cost: item.cost || 0,
      taxable: Boolean(item.taxable),
      sku: item.sku || "",
      active: item.active !== false,
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setEditForm(null);
  }

  async function saveEdit(itemId) {
    try {
      const res = await fetch(`/api/products-services/${itemId}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "x-tenant":
            window.location.hostname === "localhost"
              ? "dfasio"
              : window.location.hostname.split(".")[0] || "root",
        },
        body: JSON.stringify(editForm),
      });

      if (!res.ok) {
        alert("Failed to update item");
        return;
      }

      setEditingId(null);
      setEditForm(null);
      loadItems();
    } catch (err) {
      console.error(err);
      alert("Failed to update item");
    }
  }

  async function deleteItem(itemId) {
    if (!window.confirm("Delete this Products & Services item?")) return;

    try {
      const res = await fetch(`/api/products-services/${itemId}`, {
        method: "DELETE",
        credentials: "include",
        headers: {
          "x-tenant":
            window.location.hostname === "localhost"
              ? "dfasio"
              : window.location.hostname.split(".")[0] || "root",
        },
      });

      if (!res.ok) {
        alert("Failed to delete item");
        return;
      }

      loadItems();
    } catch (err) {
      console.error(err);
      alert("Failed to delete item");
    }
  }

  async function createItem(e) {
    e.preventDefault();

    try {
      const res = await fetch("/api/products-services/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "x-tenant":
            window.location.hostname === "localhost"
              ? "dfasio"
              : window.location.hostname.split(".")[0] || "root",
        },
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        alert("Failed to create item");
        return;
      }

      setForm({
        item_type: "service",
        name: "",
        description: "",
        unit: "",
        default_quantity: 1,
        default_rate: 0,
        cost: 0,
        taxable: false,
        sku: "",
        active: true,
      });

      loadItems();
    } catch (err) {
      console.error(err);
    }
  }

  const inputStyle = {
    width: "100%",
    padding: "12px 14px",
    borderRadius: 8,
    border: "1px solid #27496b",
    background: "#0b1f35",
    color: "#ffffff",
    outline: "none",
    boxSizing: "border-box",
  };

  const actionBtn = {
    marginTop: 16,
    padding: "12px 18px",
    borderRadius: 8,
    border: "none",
    background: "#e0b84f",
    color: "#0b1b2b",
    fontWeight: 800,
    cursor: "pointer",
  };

  return (
    <HeadquartersPage
      title="Products & Services"
      subtitle="Manage reusable services, pricing, SKUs, and operational estimating resources."
    >
      <HeadquartersSection title="Operations Summary">
        <div style={{ marginBottom: 18 }}>
          <StatusPill variant="info" dot>
            Service Intelligence Active
          </StatusPill>
        </div>

        <HeadquartersGrid min={180}>
          <HeadquartersMetricCard
            label="Saved Items"
            value={items.length}
            subtext="Reusable estimate and invoice resources"
          />

          <HeadquartersMetricCard
            label="Item Type"
            value="Services"
            subtext="Products support can expand later"
          />

          <HeadquartersMetricCard
            label="Workflow Usage"
            value="Estimates"
            subtext="Connected to estimating workflows"
          />
        </HeadquartersGrid>
      </HeadquartersSection>

      <HeadquartersSection
        title="Create Service Item"
        subtitle="Create reusable pricing and operational line items."
      >
        <form onSubmit={createItem}>
          <HeadquartersGrid min={220}>
            <input
              style={inputStyle}
              placeholder="Name"
              value={form.name}
              onChange={(e) =>
                setForm({ ...form, name: e.target.value })
              }
            />

            <input
              style={inputStyle}
              placeholder="Description"
              value={form.description}
              onChange={(e) =>
                setForm({
                  ...form,
                  description: e.target.value,
                })
              }
            />

            <input
              style={inputStyle}
              placeholder="Unit"
              value={form.unit}
              onChange={(e) =>
                setForm({ ...form, unit: e.target.value })
              }
            />

            <input
              style={inputStyle}
              type="number"
              placeholder="Rate"
              value={form.default_rate}
              onChange={(e) =>
                setForm({
                  ...form,
                  default_rate: e.target.value,
                })
              }
            />

            <input
              style={inputStyle}
              placeholder="SKU"
              value={form.sku}
              onChange={(e) =>
                setForm({ ...form, sku: e.target.value })
              }
            />
          </HeadquartersGrid>

          <button type="submit" style={actionBtn}>
            Save Item
          </button>
        </form>
      </HeadquartersSection>

      <HeadquartersSection
        title="Service Library"
        subtitle="Saved operational pricing resources available for estimates and invoices."
      >
        {loading ? (
          <div style={{ color: "#9fb0c3" }}>Loading...</div>
        ) : items.length === 0 ? (
          <div style={{ color: "#9fb0c3" }}>No items yet.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: 760,
              }}
            >
              <thead>
                <tr>
                  <th style={th}>Name</th>
                  <th style={th}>Type</th>
                  <th style={th}>Unit</th>
                  <th style={th}>SKU</th>
                  <th style={th}>Rate</th>
                  <th style={th}>Edit</th>
                  <th style={th}>Delete</th>
                </tr>
              </thead>

              <tbody>
                {items.map((item) =>
                  editingId === item.id ? (
                    <tr key={item.id}>
                      <td style={td}>
                        <input
                          style={inputStyle}
                          value={editForm.name}
                          onChange={(e) =>
                            setEditForm({ ...editForm, name: e.target.value })
                          }
                        />
                      </td>
                      <td style={td}>
                        <input
                          style={inputStyle}
                          value={editForm.item_type}
                          onChange={(e) =>
                            setEditForm({ ...editForm, item_type: e.target.value })
                          }
                        />
                      </td>
                      <td style={td}>
                        <input
                          style={inputStyle}
                          value={editForm.unit}
                          onChange={(e) =>
                            setEditForm({ ...editForm, unit: e.target.value })
                          }
                        />
                      </td>
                      <td style={td}>
                        <input
                          style={inputStyle}
                          value={editForm.sku}
                          onChange={(e) =>
                            setEditForm({ ...editForm, sku: e.target.value })
                          }
                        />
                      </td>
                      <td style={td}>
                        <input
                          style={inputStyle}
                          type="number"
                          value={editForm.default_rate}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              default_rate: e.target.value,
                            })
                          }
                        />
                      </td>
                      <td style={td}>
                        <button
                          type="button"
                          style={smallPrimaryBtn}
                          onClick={() => saveEdit(item.id)}
                        >
                          Save
                        </button>
                      </td>
                      <td style={td}>
                        <button
                          type="button"
                          style={smallSecondaryBtn}
                          onClick={cancelEdit}
                        >
                          Cancel
                        </button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={item.id}>
                      <td style={td}>{item.name}</td>
                      <td style={td}>{item.item_type}</td>
                      <td style={td}>{item.unit}</td>
                      <td style={td}>{item.sku || "—"}</td>
                      <td style={td}>${item.default_rate}</td>
                      <td style={td}>
                        <button
                          type="button"
                          style={smallSecondaryBtn}
                          onClick={() => startEdit(item)}
                        >
                          Edit
                        </button>
                      </td>
                      <td style={td}>
                        <button
                          type="button"
                          style={smallDangerBtn}
                          onClick={() => deleteItem(item.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        )}
      </HeadquartersSection>
    </HeadquartersPage>
  );
}

const smallPrimaryBtn = {
  padding: "7px 10px",
  borderRadius: 8,
  border: "none",
  background: "#e0b84f",
  color: "#0b1b2b",
  fontWeight: 800,
  cursor: "pointer",
};

const smallSecondaryBtn = {
  padding: "7px 10px",
  borderRadius: 8,
  border: "1px solid #27496b",
  background: "transparent",
  color: "#ffffff",
  fontWeight: 700,
  cursor: "pointer",
};

const smallDangerBtn = {
  padding: "7px 10px",
  borderRadius: 8,
  border: "1px solid #ef4444",
  background: "transparent",
  color: "#ef4444",
  fontWeight: 700,
  cursor: "pointer",
};

const th = {
  textAlign: "left",
  padding: "12px 10px",
  color: "#9fb0c3",
  fontSize: 12,
  borderBottom: "1px solid rgba(255,255,255,0.08)",
  whiteSpace: "nowrap",
};

const td = {
  padding: "12px 10px",
  borderBottom: "1px solid rgba(255,255,255,0.06)",
  verticalAlign: "top",
  fontSize: 13,
  color: "#ffffff",
};
